import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { pricingPlans } from '../mockData';
import { Check, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import { useNavigate } from 'react-router-dom';

const Pricing = () => {
  const navigate = useNavigate();
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true, 
    align: 'center',
    skipSnaps: false,
    dragFree: false
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <section className="py-20 bg-gradient-to-br from-indigo-50 to-blue-50 relative">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h3 className="text-blue-600 font-semibold text-lg mb-4 tracking-wide uppercase">Our Pricing</h3>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Affordable Plans for Your <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Business Needs</span>
          </h2>
        </div>

        {/* Carousel Container */}
        <div className="relative max-w-7xl mx-auto">
          {/* Navigation Buttons */}
          <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-xl border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-300 flex items-center justify-center -ml-6 transform hover:scale-110"
            aria-label="Previous plan"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-xl border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-300 flex items-center justify-center -mr-6 transform hover:scale-110"
            aria-label="Next plan"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Embla Carousel */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-8">
              {pricingPlans.map((plan, index) => (
                <div 
                  key={plan.id}
                  className="flex-[0_0_100%] min-w-0 md:flex-[0_0_calc(50%-1rem)] lg:flex-[0_0_calc(33.333%-1.5rem)]"
                >
                  <Card 
                    className={`relative overflow-hidden transition-all duration-500 hover:shadow-2xl transform hover:-translate-y-2 h-full flex flex-col ${
                      plan.popular 
                        ? 'border-4 border-blue-500 scale-105' 
                        : 'border-2 border-gray-200'
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-bl-lg font-semibold flex items-center space-x-2 z-10">
                        <Star className="w-4 h-4 fill-current" />
                        <span className="text-sm">Popular</span>
                      </div>
                    )}
                    
                    <CardHeader className="text-center pb-6 pt-10">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                      <div className="mb-4">
                        <span className="text-5xl font-bold text-blue-600">${plan.price}</span>
                      </div>
                      <p className="text-gray-600 text-sm">{plan.description}</p>
                    </CardHeader>
                    
                    <CardContent className="flex-1 flex flex-col">
                      {/* Scrollable Features Area */}
                      <div className="flex-1 overflow-y-auto mb-6 pr-2 custom-scrollbar" style={{ maxHeight: '320px' }}>
                        <ul className="space-y-3">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start space-x-3">
                              <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="mt-auto">
                        <p className="text-center text-sm font-semibold text-blue-700 mb-6">{plan.note}</p>
                        
                        {/* Choose Plan Button */}
                        <Button 
                          onClick={() => navigate(`/checkout?plan=${encodeURIComponent(plan.name)}&amount=${plan.price}&service=General Service`)}
                          className={`w-full py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                            plan.popular
                              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
                              : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
                          }`}
                        >
                          Choose This Plan
                        </Button>
                        
                        {/* Or Contact Button */}
                        <Button 
                          onClick={() => navigate('/contact')}
                          className="w-full mt-3 py-4 text-base rounded-full shadow-md hover:shadow-lg transition-all duration-300 bg-white text-gray-600 border-2 border-gray-300 hover:bg-gray-50"
                        >
                          Contact Us
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center items-center space-x-3 mt-12">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={`transition-all duration-300 rounded-full ${
                index === selectedIndex
                  ? 'w-12 h-3 bg-gradient-to-r from-blue-600 to-indigo-600'
                  : 'w-3 h-3 bg-gray-300 hover:bg-blue-300'
              }`}
              aria-label={`Go to plan ${index + 1}`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="text-center mt-6 text-gray-500 text-sm">
          <p>← Swipe or use arrows to view all plans →</p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;